// TableLamp
// Features:
// - Interactive lamp with emissive material toggling
// - Physics collider for interaction
// - Click sound effect

import { useEffect, useMemo, useRef } from 'react'
import { RigidBody } from '@react-three/rapier'
import * as THREE from 'three'

import { useStore } from '../../store/useStore'

const LAMP_CONFIG = {
    intensity: 5.39,
    color: new THREE.Color('#df9b34'),
    colorOff: new THREE.Color('#aa846b'),
}

export default function TableLamp({ nodes, physicsMaterial }) {
    const switchAudioRef = useRef()

    const lampOn = useStore((state) => state.lampOn)
    const isSoundOn = useStore((state) => state.isSoundOn)

    // Materials
    const tableLampMaterial = useMemo(() => {
        const col = lampOn ? LAMP_CONFIG.color : LAMP_CONFIG.colorOff
        const emi = lampOn ? LAMP_CONFIG.intensity : 1.0

        return new THREE.MeshStandardMaterial({
            color: col,
            emissive: col,
            emissiveIntensity: emi,
        })
    }, [lampOn])

    // Interaction: Toggle lamp
    useEffect(() => {
        // Setup audio
        const audio = new Audio('/sounds/switch.mp3')
        audio.preload = 'auto'
        audio.volume = isSoundOn ? 0.1 : 0
        switchAudioRef.current = audio

        const onMouseDown = (e) => {
            if (e.button !== 0) return
            const state = useStore.getState()

            if (
                state.currentHitName === 'Table Lamp' &&
                state.isPointerLocked
            ) {
                state.toggleLamp()

                if (switchAudioRef.current && useStore.getState().isSoundOn) {
                    switchAudioRef.current.currentTime = 0
                    switchAudioRef.current.play().catch(() => {})
                }
            }
        }

        window.addEventListener('mousedown', onMouseDown)
        return () => {
            window.removeEventListener('mousedown', onMouseDown)
            if (switchAudioRef.current) {
                switchAudioRef.current.pause()
                switchAudioRef.current = null
            }
        }
    }, [])

    // Update volume when global setting changes
    useEffect(() => {
        if (switchAudioRef.current) {
            switchAudioRef.current.volume = isSoundOn ? 0.1 : 0
        }
    }, [isSoundOn])

    return (
        <group>
            <RigidBody
                colliders="trimesh"
                type="fixed"
                userData={{ name: 'Table Lamp' }}
                position={[-0.842, 1.406, 3.528]}
            >
                <mesh
                    geometry={nodes.table_lamp_physics.geometry}
                    material={physicsMaterial}
                />
            </RigidBody>
            <mesh
                geometry={nodes.light_lamp.geometry}
                material={tableLampMaterial}
                position={[-0.842, 1.406, 3.528]}
            />
        </group>
    )
}
